import React, { useState } from 'react';
import { Search, Plus, Eye, RefreshCw, FileText } from 'lucide-react';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

const initialKnowledgeBases = [
  { id: 1, name: 'Product Documentation', files: [
    { name: 'product_overview.pdf', status: 'Active', date: '2024-01-15' },
    { name: 'installation_guide.pdf', status: 'Active', date: '2024-01-16' }
  ], status: 'Active', date: '2024-01-15', lastUpdated: '2024-01-20' },
  { id: 2, name: 'Customer Support KB', files: [
    { name: 'faq.docx', status: 'Active', date: '2024-01-10' }
  ], status: 'Active', date: '2024-01-10', lastUpdated: '2024-01-18' },
  { id: 3, name: 'Technical Guides', files: [], status: 'Active', date: '2024-01-05', lastUpdated: '2024-01-15' },
  { id: 4, name: 'Training Materials', files: [], status: 'Inactive', date: '2024-01-20', lastUpdated: '2024-01-25' },
  { id: 5, name: 'API Documentation', files: [], status: 'Active', date: '2024-01-12', lastUpdated: '2024-01-22' },
  { id: 6, name: 'FAQ Database', files: [], status: 'Active', date: '2024-01-21', lastUpdated: '2024-01-26' },
  { id: 7, name: 'Legal Documents', files: [], status: 'Active', date: '2024-01-23', lastUpdated: '2024-01-27' },
  { id: 8, name: 'Marketing Content', files: [], status: 'Inactive', date: '2024-01-24', lastUpdated: '2024-01-28' },
  { id: 9, name: 'Development Notes', files: [], status: 'Active', date: '2024-01-25', lastUpdated: '2024-01-29' },
  { id: 10, name: 'User Manuals', files: [], status: 'Active', date: '2024-01-26', lastUpdated: '2024-01-30' },
  { id: 11, name: 'Troubleshooting KB', files: [], status: 'Active', date: '2024-01-27', lastUpdated: '2024-01-31' },
  { id: 12, name: 'Release Notes', files: [], status: 'Active', date: '2024-01-28', lastUpdated: '2024-02-01' },
];

const KnowledgeBase = () => {
  const [knowledgeBases, setKnowledgeBases] = useState(initialKnowledgeBases);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingKB, setViewingKB] = useState(null);
  const [itemsPerPage] = useState(10);
  const [uploadingFile, setUploadingFile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const filteredKnowledgeBases = knowledgeBases.filter(kb =>
    kb.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentKnowledgeBases = filteredKnowledgeBases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredKnowledgeBases.length / itemsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddKnowledgeBase = (e) => {
    e.preventDefault();
    const newKB = {
      id: knowledgeBases.length + 1,
      name: formData.name,
      files: [],
      status: 'Active',
      date: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    setKnowledgeBases(prev => [...prev, newKB]);
    setFormData({
      name: '',
      description: ''
    });
    setShowAddModal(false);
  };

  const handleRefresh = () => {
    // Simulate refresh by updating last updated time
    setKnowledgeBases(prev => prev.map(kb => ({
      ...kb,
      lastUpdated: new Date().toISOString().split('T')[0]
    })));
  };

  const openViewModal = (kb) => {
    setViewingKB(kb);
    setShowViewModal(true);
    setUploadingFile(null);
  };

  const handleCloseModal = () => {
    setFormData({
      name: '',
      description: ''
    });
    setShowAddModal(false);
    setViewingKB(null);
    setShowViewModal(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const newFile = {
      name: file.name,
      status: 'Active',
      date: new Date().toISOString().split('T')[0]
    };
    setKnowledgeBases(prev => prev.map(kb => {
      if (kb.id === viewingKB.id) {
        return {
          ...kb,
          files: [...kb.files, newFile],
          lastUpdated: new Date().toISOString().split('T')[0]
        };
      }
      return kb;
    }));
    setViewingKB(prev => ({
      ...prev,
      files: [...prev.files, newFile],
      lastUpdated: new Date().toISOString().split('T')[0]
    }));
    setUploadingFile(null);
  };

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#fafbfc', 
      minHeight: '100vh',
      padding: '2rem 1.5rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Knowledge Base</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage and organize your knowledge base collections</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}
            onClick={handleRefresh}
          >
            <RefreshCw size={16} />
            <span className="d-none d-sm-inline">Refresh</span>
          </button>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add Knowledge Base</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: accentLight, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: accent, fontSize: '1rem', fontWeight: '600' }}>{knowledgeBases.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Knowledge Bases</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>All collections</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#16a34a', fontSize: '1rem', fontWeight: '600' }}>{knowledgeBases.filter(kb => kb.status === 'Active').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Active Knowledge Bases</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Currently active</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#0284c7', fontSize: '1rem', fontWeight: '600' }}>{knowledgeBases.reduce((sum, kb) => sum + kb.files.length, 0)}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Files</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>All uploaded files</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#dc2626', fontSize: '1rem', fontWeight: '600' }}>{filteredKnowledgeBases.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Filtered Results</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Search results</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="position-relative">
                <Search 
                  size={18} 
                  style={{ color: '#9ca3af', position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} 
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search knowledge bases by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '48px', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right', flex: 'none' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Showing {filteredKnowledgeBases.length} of {knowledgeBases.length} knowledge bases
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Base Table */}
      <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', width: '100%' }}>
        <div className="table-responsive">
          <table className="table mb-0" style={{ width: '100%' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>
                  Knowledge Base Name
                </th>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>
                  Number of Files
                </th>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>
                  Status
                </th>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>
                  Date Created
                </th>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>
                  Last Updated
                </th>
                <th className="border-0 fw-semibold text-muted py-2 text-end px-2" style={{ fontSize: '0.875rem' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentKnowledgeBases.map((kb, index) => (
                <tr key={kb.id} style={{ borderBottom: index === currentKnowledgeBases.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                  <td className="py-2 px-2">
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ width: 32, height: 32, background: `linear-gradient(135deg, ${accent} 0%, #8b5cf6 100%)`, color: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '0.875rem', flexShrink: 0 }}>
                        <FileText size={16} />
                      </div>
                      <div>
                        <div className="fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                          {kb.name}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          ID: {kb.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-2">
                    <span className="badge px-2 py-1 rounded-pill fw-semibold" style={{ background: '#f0f9ff', color: '#0284c7', fontSize: '0.75rem' }}>
                      {kb.files.length} files
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span className={`badge px-2 py-1 rounded-pill fw-semibold`} style={{ background: kb.status === 'Active' ? '#f0fdf4' : '#fef2f2', color: kb.status === 'Active' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>
                      {kb.status}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {kb.date}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {kb.lastUpdated}
                    </span>
                  </td>
                  <td className="py-2 text-end px-2">
                    <button 
                      className="btn btn-sm rounded-pill border-0"
                      style={{ 
                        color: accent, 
                        background: accentLight,
                        padding: '4px 12px',
                        fontSize: '0.75rem'
                      }}
                      title="View Files"
                      onClick={() => openViewModal(kb)}
                    >
                      <Eye size={12} className="me-1" />
                      View Files
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="card-footer bg-white border-0 py-3 py-md-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="text-muted text-center text-md-start" style={{ fontSize: '0.875rem' }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredKnowledgeBases.length)} of {filteredKnowledgeBases.length} results
            </div>
            <nav aria-label="Knowledge base pagination">
              <ul className="pagination mb-0 gap-1">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link border-0 px-2 px-md-3 py-2 rounded"
                    style={{ 
                      color: currentPage === 1 ? '#9ca3af' : accent,
                      background: 'transparent',
                      fontSize: '0.875rem'
                    }}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <span className="d-none d-sm-inline">Previous</span>
                    <span className="d-sm-none">‹</span>
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button 
                      className={`page-link border-0 px-2 px-md-3 py-2 rounded fw-semibold${currentPage === page ? '' : ''}`}
                      style={{ 
                        color: currentPage === page ? '#fff' : accent, 
                        background: currentPage === page ? accent : 'transparent',
                        fontSize: '0.875rem'
                      }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link border-0 px-2 px-md-3 py-2 rounded"
                    style={{ 
                      color: currentPage === totalPages ? '#9ca3af' : accent,
                      background: 'transparent',
                      fontSize: '0.875rem'
                    }}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <span className="d-sm-none">›</span>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Add Knowledge Base Modal */}
      {showAddModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 480, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Add Knowledge Base</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Create a new knowledge base collection</p>
            </div>
            <form onSubmit={handleAddKnowledgeBase}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Knowledge Base Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="name" 
                    placeholder="Enter knowledge base name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Description (Optional)</label>
                  <textarea 
                    className="form-control" 
                    name="description" 
                    placeholder="Enter description" 
                    value={formData.description} 
                    onChange={handleInputChange} 
                    rows={3}
                    style={{ 
                      background: '#f9fafb', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '0.75rem', 
                      fontSize: '0.875rem',
                      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn" 
                  onClick={handleCloseModal}
                  style={{ 
                    background: '#f8fafc', 
                    color: '#64748b', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    transition: 'all 0.15s ease-in-out'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn"
                  style={{ 
                    background: accent, 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)',
                    transition: 'all 0.15s ease-in-out'
                  }}
                >
                  Create Knowledge Base
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Files Modal */}
      {showViewModal && viewingKB && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 600, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Files in {viewingKB.name}</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>View uploaded files and their status</p>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ width: 48, height: 48, background: `linear-gradient(135deg, ${accent} 0%, #8b5cf6 100%)`, color: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '1.125rem' }}>
                  <FileText size={20} />
                </div>
                <div>
                  <h5 style={{ color: '#1f2937', fontSize: '1.125rem', fontWeight: '600', margin: '0 0 0.25rem 0' }}>{viewingKB.name}</h5>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{viewingKB.files.length} files uploaded</p>
                </div>
              </div>
              {/* File Upload */}
              <div style={{ marginBottom: '1rem' }}>
                <input type="file" onChange={handleFileUpload} style={{ marginBottom: 8 }} />
              </div>
              {/* Files Table */}
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 8, maxHeight: 200, overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', color: '#6b7280', fontWeight: 500, padding: 4 }}>File Name</th>
                      <th style={{ textAlign: 'left', color: '#6b7280', fontWeight: 500, padding: 4 }}>Status</th>
                      <th style={{ textAlign: 'left', color: '#6b7280', fontWeight: 500, padding: 4 }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingKB.files && viewingKB.files.length > 0 ? (
                      viewingKB.files.map((file, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: 4 }}>{file.name}</td>
                          <td style={{ padding: 4 }}>
                            <span className="badge px-2 py-1 rounded-pill fw-semibold" style={{ background: file.status === 'Active' ? '#f0fdf4' : '#fef2f2', color: file.status === 'Active' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>{file.status}</span>
                          </td>
                          <td style={{ padding: 4 }}>{file.date}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={3} style={{ color: '#9ca3af', textAlign: 'center', padding: 8 }}>No files uploaded yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* End Files Table */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>Status</span>
                  <span className={`badge px-2 py-1 rounded-pill fw-semibold`} style={{ background: viewingKB.status === 'Active' ? '#f0fdf4' : '#fef2f2', color: viewingKB.status === 'Active' ? '#16a34a' : '#dc2626', fontSize: '0.75rem' }}>
                    {viewingKB.status}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>Created</span>
                  <span style={{ color: '#1f2937', fontSize: '0.875rem', fontWeight: '500' }}>{viewingKB.date}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>Last Updated</span>
                  <span style={{ color: '#1f2937', fontSize: '0.875rem', fontWeight: '500' }}>{viewingKB.lastUpdated}</span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="btn"
                onClick={handleCloseModal}
                style={{ 
                  background: accent, 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  padding: '0.75rem 1.5rem', 
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)',
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase; 